// rate-core.js — generatore PURO di piani di pagamento a tranche (fatt-v1).
// NIENTE React, NIENTE DOM, NIENTE side-effect, NIENTE new Date() senza
// argomenti: tutta l'aritmetica di calendario è su stringhe ISO / Date.UTC
// deterministico. Gli importi sono trattati internamente in CENTESIMI interi
// (Math.round half-up, coerente con r2 di fatt-calc): la somma delle rate è
// SEMPRE identica all'importoTotale al centesimo.
//
// generaRate(params) → { rate: [{dataScadenza:'YYYY-MM-DD', importo:number}], errore?:string }
//   params: {
//     importoTotale: number > 0,
//     modo: 'numero' | 'importo',
//     numeroRate?:  int ≥ 1        (modo 'numero'),
//     importoRata?: number > 0     (modo 'importo'),
//     dataPrima: 'YYYY-MM-DD',
//     cadenza: 'mensile' | 'bimestrale' | 'trimestrale',
//     giornoDelMese?: 1..28 | null (null = stesso giorno di dataPrima;
//                                   29/30/31 NON ammessi per non scavallare i mesi corti)
//   }
// In caso di parametri invalidi NON lancia: ritorna { rate: [], errore: '...' }.

export const MAX_RATE = 60;

const PASSO_CADENZA = { mensile: 1, bimestrale: 2, trimestrale: 3 };

// --- Helper puri -------------------------------------------------------------

// Arrotondamento matematico a 2 decimali (half-up sulla 3ª cifra) — come
// r2 di server/src/lib/fatt-calc.js.
function r2(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  const scaled = Number((Math.abs(n) * 100).toPrecision(15));
  return sign * (Math.round(scaled) / 100);
}

// number → centesimi interi (half-up), NaN/Infinity → null.
function toCents(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return null;
  const sign = n < 0 ? -1 : 1;
  const scaled = Number((Math.abs(n) * 100).toPrecision(15));
  return sign * Math.round(scaled);
}

function centsToEuro(c) { return c / 100; }

// Data ISO YYYY-MM-DD esistente sul calendario (29/02 solo bisestili, ecc.).
function dataIsoValida(s) {
  const v = String(s == null ? '' : s);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const y = +v.slice(0, 4), m = +v.slice(5, 7), d = +v.slice(8, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const t = new Date(Date.UTC(y, m - 1, d)); // solo aritmetica di calendario, NON legge l'orologio
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

function giorniNelMese(anno, mese1a12) {
  // giorno 0 del mese successivo = ultimo giorno del mese richiesto (aritmetica UTC pura).
  return new Date(Date.UTC(anno, mese1a12, 0)).getUTCDate();
}

function pad2(n) { return n < 10 ? '0' + n : String(n); }

// dataPrima + (mesi) con giorno desiderato: PRIMA si sposta il mese (aritmetica
// su anno/mese, mai overflow del giorno), POI si fissa il giorno clampato alla
// lunghezza del mese di arrivo (mesi corti: 31/01 + 1 mese → 28/02, non 03/03).
function dataRata(y, m, giorno, mesiAvanti) {
  const idx = (m - 1) + mesiAvanti;
  const anno = y + Math.floor(idx / 12);
  const mese = ((idx % 12) + 12) % 12 + 1;
  const g = Math.min(giorno, giorniNelMese(anno, mese));
  return anno + '-' + pad2(mese) + '-' + pad2(g);
}

// --- API ---------------------------------------------------------------------

export function generaRate(params) {
  const p = params || {};
  const ko = (msg) => ({ rate: [], errore: msg });

  // Importo totale
  const totaleC = toCents(p.importoTotale);
  if (totaleC == null || totaleC <= 0) {
    return ko("L'importo totale deve essere un numero maggiore di zero");
  }

  // Data prima rata
  if (!dataIsoValida(p.dataPrima)) {
    return ko('La data della prima rata non è valida (formato AAAA-MM-GG)');
  }
  const y = +String(p.dataPrima).slice(0, 4);
  const m = +String(p.dataPrima).slice(5, 7);
  const d = +String(p.dataPrima).slice(8, 10);

  // Cadenza
  const passo = PASSO_CADENZA[p.cadenza];
  if (!passo) return ko('La cadenza deve essere mensile, bimestrale o trimestrale');

  // Giorno del mese: null/assente = stesso giorno di dataPrima (clampato ai mesi
  // corti); se indicato deve essere 1..28 (29/30/31 non ammessi).
  let giorno;
  if (p.giornoDelMese == null) {
    giorno = d;
  } else {
    const g = Number(p.giornoDelMese);
    if (!Number.isInteger(g) || g < 1 || g > 28) {
      return ko('Il giorno del mese deve essere un numero tra 1 e 28 (per i giorni 29-31 usare 28)');
    }
    giorno = g;
  }

  // Importi delle rate (in centesimi, ultima = resto esatto)
  let importiC;
  if (p.modo === 'numero') {
    const n = Number(p.numeroRate);
    if (!Number.isInteger(n) || n < 1) {
      return ko('Il numero di rate deve essere un intero maggiore o uguale a 1');
    }
    if (n > MAX_RATE) return ko('Troppe rate: il massimo è ' + MAX_RATE);
    const baseC = Math.round(totaleC / n); // half-up sui centesimi = r2(totale/n)
    const ultimaC = totaleC - baseC * (n - 1);
    if (ultimaC <= 0 && n > 1) {
      return ko("L'importo totale è troppo piccolo per essere diviso in " + n + ' rate');
    }
    importiC = [];
    for (let i = 0; i < n - 1; i++) importiC.push(baseC);
    importiC.push(ultimaC);
  } else if (p.modo === 'importo') {
    const rataC = toCents(p.importoRata);
    if (rataC == null || rataC <= 0) {
      return ko("L'importo della rata deve essere un numero maggiore di zero");
    }
    const piene = Math.floor(totaleC / rataC);
    const restoC = totaleC - piene * rataC;
    const n = piene + (restoC > 0 ? 1 : 0);
    if (n > MAX_RATE) return ko('Troppe rate: il massimo è ' + MAX_RATE);
    importiC = [];
    for (let i = 0; i < piene; i++) importiC.push(rataC);
    if (restoC > 0) importiC.push(restoC); // resto 0 → nessuna rata in più
  } else {
    return ko("Il modo deve essere 'numero' (rate uguali) o 'importo' (importo fisso per rata)");
  }

  // Date: prima rata = dataPrima esatta, successive = mese + i*passo con giornoDelMese.
  const rate = importiC.map((imp, i) => ({
    dataScadenza: i === 0 ? String(p.dataPrima) : dataRata(y, m, giorno, i * passo),
    importo: centsToEuro(imp),
  }));

  return { rate };
}

// Somma degli importi delle rate, arrotondata a 2 decimali (in centesimi,
// per non accumulare errori binari). Rate malformate/importi non numerici → 0.
export function sommaRate(rate) {
  if (!Array.isArray(rate)) return 0;
  let cents = 0;
  for (const r of rate) {
    const c = r == null ? null : toCents(r.importo);
    if (c != null) cents += c;
  }
  return centsToEuro(cents);
}

export { r2 };
